import React from "react";
import { Compass, Leaf, Sprout, Users } from "lucide-react";

import { SectionPanel } from "@/components/platform/ContentPrimitives";
import { PLATFORM_NAME } from "@/lib/polenica";

const CONTENT = [
  {
    id: "quienes-somos",
    eyebrow: "Quiénes somos",
    title: `${PLATFORM_NAME} es una red viva de huertas, pacas y saberes.`,
    icon: Leaf,
    text: [
      "Polénica es una red viva para conectar huertas, pacas urbanas, saberes, productos, servicios y encuentros que están polinizando la agroecología urbana en Medellín y el Valle de Aburrá.",
      "Este espacio nace para visibilizar iniciativas, fortalecer procesos comunitarios y facilitar el encuentro entre personas, colectivos, instituciones educativas y organizaciones que trabajan por el cuidado de la vida, la gestión de residuos orgánicos, la soberanía alimentaria y la construcción de territorios más sostenibles.",
      "Más que una plataforma, Polénica busca ser un lugar donde las experiencias circulen, los aprendizajes se compartan y nuevas conexiones sigan fortaleciendo la red agroecológica de la ciudad.",
    ],
  },
  {
    id: "sobre-el-proyecto",
    eyebrow: "Sobre el proyecto",
    title: "Una herramienta territorial dentro de un proceso más amplio.",
    icon: Sprout,
    text: [
      "Polénica hace parte del proyecto Medellín resiliente ante el cambio climático, colegios medioambientalmente sostenibles, una apuesta que promueve la agricultura agroecológica urbana, la educación ambiental, el aprovechamiento de residuos orgánicos y la participación comunitaria como caminos para fortalecer la resiliencia de los territorios.",
      "Este proyecto es impulsado por Asamblea de Cooperación por la Paz (ACPP) y Fundación Forjando Futuros (FFF), con financiación del Ayuntamiento de Madrid, en articulación con comunidades educativas, iniciativas urbanas y diversos actores del territorio.",
      "En este contexto, Polénica surge como una herramienta para hacer visibles las huertas y pacas urbanas de la ciudad, compartir conocimientos, facilitar encuentros, conectar productos y servicios, y fortalecer los procesos que hoy están sembrando alternativas para el cuidado de la vida.",
    ],
  },
  {
    id: "como-participar",
    eyebrow: "Cómo participar",
    title: "Hay muchas formas de hacer parte de la red.",
    icon: Users,
    text: [
      "Si integras una huerta, una paca urbana o una iniciativa relacionada con la agroecología, puedes registrarla en el mapa para que otras personas la conozcan y se conecten con ella.",
      "También puedes compartir productos o servicios en el directorio, publicar eventos en la agenda, participar en el foro comunitario, aportar materiales a la biblioteca o poner en circulación tus preguntas y experiencias.",
      "Si apenas estás comenzando a acercarte a estos temas, también puedes recorrer la plataforma como visitante, explorar iniciativas, descubrir actividades y conocer experiencias que ya están transformando el territorio.",
      "Polénica está pensada para quienes cultivan, enseñan, aprenden, comparten, investigan o simplemente desean acercarse a la agroecología urbana y a las comunidades que la hacen posible.",
    ],
  },
];

export default function AboutPage() {
  return (
    <div className="mx-auto max-w-6xl p-6">
      <section className="platform-hero-banner overflow-hidden rounded-[36px] px-6 py-8 sm:px-8">
        <div className="flex max-w-4xl items-start gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-[20px] bg-primary text-primaryForeground shadow-[0_16px_30px_rgba(41,64,45,0.18)]">
            <Compass className="h-6 w-6" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-primary/70">Página complementaria</p>
            <h1 className="mt-3 font-heading text-4xl font-semibold text-primary sm:text-5xl">
              Quiénes somos, de dónde nace {PLATFORM_NAME} y cómo sumarse.
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-mutedForeground sm:text-base">
              Esta página reúne el contexto del proyecto, la razón de ser de la plataforma y las rutas de
              participación para personas, colectivos e iniciativas del territorio.
            </p>
          </div>
        </div>
      </section>

      <div className="mt-6 space-y-6">
        {CONTENT.map((section) => {
          const Icon = section.icon;

          return (
            <SectionPanel
              key={section.id}
              title={section.title}
              eyebrow={section.eyebrow}
              className="scroll-mt-24"
            >
              <div id={section.id} className="flex flex-col gap-5 lg:flex-row">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="space-y-4 text-sm leading-7 text-textMain sm:text-base">
                  {section.text.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </SectionPanel>
          );
        })}
      </div>
    </div>
  );
}
